import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Button, Radio, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendNewMessage } from "../../../redux/monitoring/monitoringSlice";
// INTERFACES
import {
	BankFloorInterface,
	ControlPanelElevatorInterface,
} from "../../../interfaces/EdgeManagement.interface";
import { RootState } from "../../../app/store";
// COMPONENTS
import ControlPanelCommands from "./ControlPanelCommands";
// AUTHENTICATION
import GETJwtToken from "../../../redux/authentication/authentication";

function ControlPanelElevator({
	currentFloor,
	carService,
}: ControlPanelElevatorInterface) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const dispatch = useDispatch();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [doorSelected, setDoorSelected] = useState<"FRONT" | "REAR">("FRONT");
	const [spinningFloorNo, setSpinningFloorNo] = useState<number>(0);
	const [secureCalls, setSecureCalls] = useState<boolean>(false);
	const [elevatorCurrentFloor, setElevatorCurrentFloor] = useState(0);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { liftSelected, layoutSelected, socketConnection, pannelFirstState } =
		useSelector((state: RootState) => state.monitoring);
	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [postCommand, { isLoading: isLoadingPostCommand }] =
		usePostMonitoringCommandMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickCarCall = async (floor: BankFloorInterface) => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: {
				lift_id: liftSelected.id,
				short_msg: `${secureCalls ? "S" : ""}E${
					doorSelected === "REAR" ? "R" : ""
				}CC${floor.floor_index}`,
			},
			serverId: layoutSelected.idServer,
		};
		postCommand(BODY);
	};

	// eslint-disable-next-line no-unused-vars
	const onClickBlockUnblockElevatorEmulator = () => {
		if (socketConnection === "EMULATOR") {
			dispatch(
				updateSendNewMessage({
					bank_id: layoutSelected.id,
					lift_id: liftSelected.id,
					bankName: layoutSelected.name,
					liftName: liftSelected.name,
					msg: {
						type: "BLOCK-UNBLOCK",
						message: "block elevator",
						short_msg: "BLOCK-UNBLOCK",
					},
				})
			);
		}
	};

	const onClickCarCallEmulator = (floor: BankFloorInterface) => {
		if (socketConnection === "EMULATOR") {
			dispatch(
				updateSendNewMessage({
					bank_id: layoutSelected.id,
					lift_id: liftSelected.id,
					bankName: layoutSelected.name,
					liftName: liftSelected.name,
					msg: {
						type: "CARCALL",
						message: `car call floor No. ${floor.floor_no}`,
						short_msg: floor.floor_no,
					},
				})
			);
		}
	};

	// eslint-disable-next-line no-unused-vars
	const onChangeSecureCalls = (checked: boolean) => {
		setSecureCalls(checked);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (currentFloor) {
			setElevatorCurrentFloor(currentFloor);
		}
	}, [currentFloor]);

	useEffect(() => {
		if (pannelFirstState && pannelFirstState.position) {
			setElevatorCurrentFloor(pannelFirstState.position);
		}
	}, [pannelFirstState]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div
			className='monitoringView__detailsBox'
			style={{ minHeight: "400px", height: "auto" }}
		>
			<div
				className={`montoringView__controlPanel montoringView__controlPanel__${theme}`}
				style={{
					minHeight: "400px",
					height: "auto",
					paddingBottom: "25px",
				}}
			>
				{/** ACTIONS */}
				<div className={` drawer__box__${theme} mt-3`}>
					<div className='generalStyles__spaceBetween'>
						<div className='generalStyles__flex'>
							<Radio.Group
								value={doorSelected}
								onChange={(e) => {
									setDoorSelected(e.target.value);
								}}
							>
								<Radio value='FRONT'>
									{t("configuration.configurationFiles.frontDoors")}
								</Radio>
								<Radio value='REAR'>
									{t("configuration.configurationFiles.rearDoors")}
								</Radio>
							</Radio.Group>
						</div>
						<div>
							{secureCalls && (
								<FontAwesomeIcon
									icon={faLock}
									fontSize={18}
									className='monitoring__modalSettings__icon generalStyles__mrFix'
								/>
							)}
							{/**
               * <span
								className='generalStyles__mrFix'
								style={{ marginTop: "5px" }}
							>
								{t("general.secureCalls")}
							</span> 
               * <Switch checked={secureCalls} onChange={onChangeSecureCalls} /> 
               * */}
						</div>
					</div>
				</div>
				{/** PANEL */}
				<div className={`drawer__box__${theme} mt-3`}>
					<div className='monitoringView__controlPanelPanel'>
						{layoutSelected.bank_floors.map((floor: BankFloorInterface) => (
							<div key={floor.id}>
								<Button
									className={`monitoring__modalSettings__button__1 ${
										liftSelected && elevatorCurrentFloor === floor.floor_no
											? `monitoringView__controlPanelNumber__active__${theme}`
											: `monitoringView__controlPanelNumber__${theme}`
									}`}
									shape='circle'
									onClick={() => {
										onClickCarCall(floor);
										onClickCarCallEmulator(floor);
										setSpinningFloorNo(floor.floor_no);
									}}
									disabled={
										(doorSelected === "FRONT" && !floor.front_door) ||
										(doorSelected === "REAR" && !floor.rear_door)
									}
								>
									{isLoadingPostCommand &&
									spinningFloorNo === floor.floor_no ? (
										<Spin
											spinning={
												isLoadingPostCommand &&
												spinningFloorNo === floor.floor_no
											}
										/>
									) : (
										<span style={{ marginTop: "3px" }}>
											{floor && floor.name && floor.name !== ""
												? floor.name.substring(0, 3).toUpperCase()
												: floor.floor_index}
										</span>
									)}
								</Button>{" "}
							</div>
						))}
					</div>
				</div>
				{/* ADVANCE ACTIONS */}
				<ControlPanelCommands carService={carService} />
			</div>
		</div>
	);
}

export default ControlPanelElevator;
